/**
 * Coverage Policy vocabulary: one label per contract path, the reason-code
 * labels, and the corrected copy of SPEC Section 12 (td-10, Phase 1 Step 1).
 *
 * Source of every string below:
 *   sprint-queue/sprint-member-liquidity-v2/coverage-policy-redesign/SPEC.md,
 *   Sections 3, 4, 6.3, 6.4, 8.4, 10 and 12. Where the post-verify corrections
 *   of 2026-09-22 replace a sentence, the corrected sentence is the one used:
 *   the step-down wording (corrections 2 and 3), the cap caveat (3), and the
 *   group name "Recorded, not applied today" (6).
 *
 * FIELDS is typed as a Record over every leaf path of the generated
 * LiquidityPolicyParameters, so a property the contract adds fails this file at
 * compile time until it has a label. Reason codes, tiers, pricing modes,
 * net inflow measures and data sources are Records over their generated unions
 * for the same reason.
 *
 * Voice: the scorecard computes an advisory limit; the institution decides.
 * Tier 1 strings carry no credit vocabulary and no rescue words. Tier 2 strings
 * (Section 5 of the page) may name a Tempo Line of Credit.
 *
 * Runtime imports: none. bounds.ts, draft.ts, plain-language.ts and
 * preview-model.ts read from this file, and this file reads only types. The
 * page's own vocabulary types (sections, disclosures, units, advisories,
 * reach kinds) are the admin-portal-ui view models
 * (schema-registry/admin-portal-ui.openapi.yaml).
 */

import type { components as AdminPortalUiComponents } from '@aaimplatform/institutional-types/generated/admin-portal-ui';
import type { components } from '@aaimplatform/institutional-types/tempo';
import type { ParameterLeafPath } from './draft';

type LiquidityReasonCode = components['schemas']['LiquidityReasonCode'];
type LiquidityCureCapBasis = components['schemas']['LiquidityCureCapBasis'];
type LiquidityDataLaneMode = components['schemas']['LiquidityDataLaneMode'];
type StandbyOfferSegment = components['schemas']['StandbyOfferSegment'];
type TempoDeliveryMode = components['schemas']['TempoDeliveryMode'];
/** The contract's inline enum on LiquidityPolicyParameters.pricingMode (tempo.openapi.yaml). */
type PricingMode = components['schemas']['LiquidityPolicyParameters']['pricingMode'];
type LinePricingStructure = components['schemas']['LinePricingStructure'];
type CoverageTier = components['schemas']['CoverageRecommendation']['tier'];

// ---------------------------------------------------------------------------
// The governing sentence (page subtitle, preview footer, evidence footer).
// ---------------------------------------------------------------------------

export const GOVERNING_SENTENCE =
  'The scorecard computes an advisory limit for each account. Your institution decides whether and how to act on it.';

// ---------------------------------------------------------------------------
// Sections and disclosures (SPEC Section 3).
// ---------------------------------------------------------------------------

export type SectionId = AdminPortalUiComponents['schemas']['CoveragePolicySectionId'];

/**
 * The page's six numbered sections plus Section 0, in the order the scorecard
 * decides. Section 5 carries the ruled name (post-verify correction 6).
 */
export const SECTIONS: readonly { readonly id: SectionId; readonly number: number; readonly heading: string }[] = [
  { id: 'how-computed', number: 0, heading: 'How an advisory limit is computed' },
  { id: 'who-qualifies', number: 1, heading: 'Who qualifies' },
  { id: 'limit-size', number: 2, heading: 'How large an advisory limit can be' },
  { id: 'cure-cap', number: 3, heading: 'Cure cap' },
  { id: 'protections', number: 4, heading: 'Protections' },
  { id: 'recorded-only', number: 5, heading: 'Recorded, not applied today' },
  { id: 'data-source', number: 6, heading: 'Scoring data source' },
];

/** Group label and badge text for the Tier 2 and workout settings (ruled 2026-09-22). */
export const RECORDED_ONLY_GROUP_LABEL = 'Recorded, not applied today';

export type DisclosureId = AdminPortalUiComponents['schemas']['CoveragePolicyDisclosureId'];

export const DISCLOSURE_LABEL: Readonly<Record<DisclosureId, string>> = {
  'more-qualification': 'More qualification settings',
  'more-sizing': 'More sizing settings',
  'more-cure-cap': 'More cure-cap settings',
  'adjust-protections': 'Adjust protections',
  'recorded-only': RECORDED_ONLY_GROUP_LABEL,
};

/** "{n} changed" on a disclosure whose contents differ from the baseline. */
export function disclosureChangedText(changedCount: number): string {
  return `${changedCount} changed`;
}

export const SECTION_COPY = {
  howComputed:
    'The scoring run covers only accounts with an active Tempo enrollment. For each of those accounts, the scorecard first runs the checks in Section 1. An account that fails any check records no advisory limit. For the rest, the scorecard takes the smallest of four amounts, rounded down to the nearest $25: the share of reliable deposits (Section 2), the cure cap (Section 3), the tier cap (Section 2), and the program ceiling (Section 2). The protections in Section 4 then apply, and can raise or lower that figure. A figure below $25 records $0.',
  whoQualifiesIntro:
    'The scoring run covers only accounts with an active Tempo enrollment. Each such account must pass every check here. An account that fails any check records no advisory limit, and the preview names the reason.',
  cureCapSubheading:
    "Could the account's normal net inflow bring a negative balance back to zero within the days you set?",
  cureCapFormula:
    'Cure cap = net inflow measure x days of net inflow x (1 - haircut). When it is the smallest of the four amounts, the advisory limit starts from it. Account holders never see these settings, and none of them is a deadline.',
  cureCapExplainerTrigger: 'How the cure cap works',
  protectionsIntro:
    'Applied after the four amounts are compared, in this order: fee freeze, step-down, review flag, data completeness. A scoring run is one score date, normally the nightly run; a rescore on the same date starts from the same previous figure.',
  recordedOnlyIntro:
    'Tier 2 is a Tempo Line of Credit: open-end credit your institution underwrites and decides on. The scorecard does not produce Tier 2 figures today, so nothing in this section changes any account or any preview figure. Values are recorded on the version for when Tier 2 is built.',
} as const;

// ---------------------------------------------------------------------------
// Field vocabulary: one entry per leaf path of LiquidityPolicyParameters.
// ---------------------------------------------------------------------------

/**
 * How a value is displayed. `fraction-percent` stores the fraction the contract
 * declares and displays percent (0.15 displays as 15%); `percent` is a contract
 * field whose unit is already percent (the APR band, where 12.5 means 12.5%).
 */
export type FieldUnit = AdminPortalUiComponents['schemas']['CoveragePolicyFieldUnit'];

/**
 * One setting's label, section, unit, optional disclosure and helper, and the
 * read-only mark on a leaf this page carries unchanged.
 */
export type FieldEntry = AdminPortalUiComponents['schemas']['CoveragePolicyFieldEntry'];

const CARRIED_HELPER =
  'Set through the program team. Carried unchanged into every version you publish here.';

const CAP_CAVEAT =
  'An account already above a lowered cap moves down to it at the step-down rate in Section 4; at 0% it stays above the cap.';

/**
 * Declaration order is section order: the diff, the review table and the
 * evidence sheet list settings in this order (FIELD_ORDER below).
 */
export const FIELDS: { readonly [P in ParameterLeafPath]: FieldEntry } = {
  // Section 1: Who qualifies
  pricingMode: {
    label: 'Tier 1 fee posture',
    section: 'who-qualifies',
    unit: 'choice',
    helper:
      'Sets whether Tier 1 coverage carries a per-item fee. On the scorecard its only effect is whether the Reg E opt-in check applies.',
  },
  'eligibility.requireRegEOptInForFeeCoverage': {
    label: 'Require the Reg E opt-in for fee-bearing coverage',
    section: 'who-qualifies',
    unit: 'switch',
    helper:
      'Under a per-item fee, an account whose core has not reported an opt-in records no advisory limit for any item type, including checks and ACH. An unknown election counts as no opt-in.',
  },
  'eligibility.minTenureDays': {
    label: 'Minimum observed history (days)',
    section: 'who-qualifies',
    unit: 'days',
    helper:
      'Days since the earliest record the platform holds for the account: its link date, first posted transaction or first balance snapshot, whichever is oldest. The account open date is not used, so a long-standing account linked last month counts from last month.',
  },
  'eligibility.excludePriorChargeOff': {
    label: 'Exclude accounts with a charge-off on record',
    section: 'who-qualifies',
    unit: 'switch',
    helper:
      "A charge-off ever recorded for this account keeps it at no advisory limit, with no end date. The holder's other accounts are not checked.",
  },
  'eligibility.minRecurringInflowScore': {
    label: 'Minimum deposit regularity',
    section: 'who-qualifies',
    unit: 'score',
    disclosure: 'more-qualification',
    helper:
      "The share of the account's 90-day deposit dollars that arrive as a repeating deposit: at least three deposits of similar size at a steady interval of 6 to 35 days. The source is not read, so income on a longer or irregular schedule, such as a quarterly pension, scores 0.",
  },
  'guardrails.coolingOffDays': {
    label: 'Wait after a charge-off (days)',
    section: 'who-qualifies',
    unit: 'days',
    disclosure: 'more-qualification',
    helper:
      'A charge-off recorded for the account starts this wait, and the account records no advisory limit until it ends. Only a charge-off starts it today, and it applies to charge-offs recorded after you publish, so the preview cannot show a change.',
  },

  // Section 2: How large an advisory limit can be
  alpha: {
    label: 'Share of reliable 30-day deposits',
    section: 'limit-size',
    unit: 'fraction-percent',
    helper:
      "Reliable deposits are the account's deposits over the last 30 days times how steady its deposits have been month to month, from 0 to 1. The scorecard takes this share of that amount; example: $3,000 x 0.80 = $2,400 reliable, and 15% of that is $360, recorded as $350.",
  },
  'tierCaps.entryCap': {
    label: 'Entry cap (USD)',
    section: 'limit-size',
    unit: 'usd',
    helper: 'The most an account can receive when its previous recommendation carried no advisory limit.',
  },
  'tierCaps.standardCap': {
    label: 'Standard cap (USD)',
    section: 'limit-size',
    unit: 'usd',
    helper: `The most a new figure can be once the account's previous recommendation carried an advisory limit above $0. ${CAP_CAVEAT}`,
  },
  programCapUsd: {
    label: 'Program ceiling (USD)',
    section: 'limit-size',
    unit: 'usd',
    disclosure: 'more-sizing',
    helper:
      'Optional. It can only lower a ceiling and must be at least the entry cap, so it changes results only for accounts at the standard tier; an account already above a lowered cap moves down to it at the step-down rate in Section 4, and at 0% it stays above the cap.',
  },

  // Section 3: Cure cap
  cureCapBasis: {
    label: 'Net inflow measure',
    section: 'cure-cap',
    unit: 'choice',
  },
  maxCureDays: {
    label: 'Days of net inflow',
    section: 'cure-cap',
    unit: 'days',
    helper:
      'How many days of the net inflow measure the cure cap allows. It sizes the advisory limit only and sets no deadline.',
  },
  cureCapHaircut: {
    label: 'Cure-cap haircut',
    section: 'cure-cap',
    unit: 'fraction-percent',
    disclosure: 'more-cure-cap',
    helper:
      'Removes this share of the cure cap before it is compared with the other three amounts. 25% keeps three quarters.',
  },

  // Section 4: Protections
  'guardrails.feeToInflowBreakerRatio': {
    label: 'Fee-to-deposit freeze point',
    section: 'protections',
    unit: 'fraction-percent',
    disclosure: 'adjust-protections',
    helper:
      "While fees over 90 days are at or above this share of deposits over the same 90 days, the advisory limit cannot rise above the previous run's figure, and an account with no previous advisory limit records $0. A lower new figure still applies, and accounts without a 90-day fee figure are not checked.",
  },
  'guardrails.stepDownDecrementRatio': {
    label: 'Largest reduction per scoring run',
    section: 'protections',
    unit: 'fraction-percent',
    disclosure: 'adjust-protections',
    helper:
      'When an account still passes every check and its new figure is lower than its previous advisory limit, one scoring run removes at most this share, including a fall from data-completeness scaling; at 25%, $500 falls no lower than $375 in that run. An account that fails a check in Section 1 records $0 at once.',
  },
  'guardrails.chronicUseEventThreshold12mo': {
    label: 'Frequent-use review flag (items in 12 months)',
    section: 'protections',
    unit: 'count',
    disclosure: 'adjust-protections',
    helper:
      "Counts items your core paid into a negative balance in the 365 days ending on the scoring run's date; at or above this count, the account is flagged for your review and its advisory limit does not change. Each scoring run counts again over its own 365 days, so items paid before that window no longer count.",
  },
  'guardrails.minDataCompletenessRatio': {
    label: 'Minimum data completeness',
    section: 'protections',
    unit: 'fraction-percent',
    disclosure: 'adjust-protections',
    helper:
      'Below this share of expected data, the advisory limit scales down to match the data present. A $500 figure at 51% completeness records $250.',
  },

  // Section 5: Recorded, not applied today
  'tierCaps.graduatedLineMultiplier': {
    label: 'Graduated line multiplier (Tier 2)',
    section: 'recorded-only',
    unit: 'multiple',
    disclosure: 'recorded-only',
    helper:
      'The size of a Tempo Line of Credit your institution may arrange for an account that qualifies for Tier 2 review, as a multiple of its standing Tier 1 advisory limit. No part of the scorecard reads this value today, and there is no platform default.',
  },
  'eligibility.maxPaymentToInflowRatio': {
    label: 'Workout payment ceiling',
    section: 'recorded-only',
    unit: 'fraction-percent',
    disclosure: 'recorded-only',
    helper:
      "For a workout arrangement, the payment may not exceed this share of the account's reliable monthly deposits. Recorded now; no engine code applies it yet.",
  },
  'workoutOffer.offerAtDaysNegative': {
    label: 'Workout offer after days negative',
    section: 'recorded-only',
    unit: 'days',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'workoutOffer.cureWindowLetterDay': {
    label: 'First cure-window letter (day)',
    section: 'recorded-only',
    unit: 'day-number',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'workoutOffer.feesStopAtOffer': {
    label: 'Fees stop at the workout offer',
    section: 'recorded-only',
    unit: 'switch',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'workoutOffer.feesStopEffectiveAt': {
    label: 'Fee stop effective date',
    section: 'recorded-only',
    unit: 'date',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'workoutOffer.offerBalanceBandUsd.minUsd': {
    label: 'Workout balance band, lower (USD)',
    section: 'recorded-only',
    unit: 'usd',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'workoutOffer.offerBalanceBandUsd.maxUsd': {
    label: 'Workout balance band, upper (USD)',
    section: 'recorded-only',
    unit: 'usd',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'workoutOffer.deMinimisWriteDownUsd': {
    label: 'Write-down threshold (USD)',
    section: 'recorded-only',
    unit: 'usd',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'lineTerms.aprBoundsPercent.minPercent': {
    label: 'APR range, lower (percent)',
    section: 'recorded-only',
    unit: 'percent',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'lineTerms.aprBoundsPercent.maxPercent': {
    label: 'APR range, upper (percent)',
    section: 'recorded-only',
    unit: 'percent',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'lineTerms.termMonths': {
    label: 'Term (months)',
    section: 'recorded-only',
    unit: 'months',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'lineTerms.minimumPaymentRatio': {
    label: 'Minimum payment (share of balance)',
    section: 'recorded-only',
    unit: 'fraction-percent',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'lineTerms.pricingStructure': {
    label: 'Line pricing structure',
    section: 'recorded-only',
    unit: 'choice',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'standbyTargeting.offerSegments': {
    label: 'Invited segments',
    section: 'recorded-only',
    unit: 'list',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'standbyTargeting.requireExternalLiquidityDescriptorMatch': {
    label: 'Descriptor match required',
    section: 'recorded-only',
    unit: 'switch',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'standbyTargeting.cureTrendThresholdDays': {
    label: 'Trend threshold (days)',
    section: 'recorded-only',
    unit: 'days',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },
  'standbyTargeting.externalLiquidityDescriptors': {
    label: 'Descriptor rules',
    section: 'recorded-only',
    unit: 'list',
    disclosure: 'recorded-only',
    helper: CARRIED_HELPER,
    readOnly: true,
  },

  // Section 6: Scoring data source
  dataLane: {
    label: 'Scoring data source',
    section: 'data-source',
    unit: 'choice',
    helper:
      "Recorded on every version and in the evidence export, so the source in force on any past date can be shown to an examiner. Consent is enforced by the platform's consent check and is not a setting here.",
  },
};

/** Every contract leaf path, in section order. */
export const FIELD_ORDER: readonly ParameterLeafPath[] = Object.keys(FIELDS) as ParameterLeafPath[];

export function isParameterLeafPath(path: string): path is ParameterLeafPath {
  return Object.prototype.hasOwnProperty.call(FIELDS, path);
}

/**
 * The label for a path a diff or an error names. A path the contract does not
 * declare, such as a property a newer contract added, reads as this sentence;
 * the page never shows a spec key in place of a label (SPEC Section 10).
 */
export const UNLISTED_SETTING_LABEL = 'A setting this page does not list';

export function fieldLabel(path: string): string {
  return isParameterLeafPath(path) ? FIELDS[path].label : UNLISTED_SETTING_LABEL;
}

// ---------------------------------------------------------------------------
// Enum labels.
// ---------------------------------------------------------------------------

/**
 * The Tier 1 fee field takes two values (GH #2929, operator ruling 8 of
 * 2026-09-22). The single-APR structure is a Tier 2 line term, labeled below.
 */
export const PRICING_MODE_LABEL: Readonly<Record<PricingMode, string>> = {
  PER_ITEM_FEE: 'Per-item fee',
  NO_PER_ITEM_FEE: 'No per-item fee',
};

/** lineTerms.pricingStructure: recorded on the version; no engine code reads it. */
export const LINE_PRICING_STRUCTURE_LABEL: Readonly<Record<LinePricingStructure, string>> = {
  SINGLE_APR_LINE: 'Single APR',
};

export const CURE_CAP_BASIS_LABEL: Readonly<Record<LiquidityCureCapBasis, string>> = {
  NET_FLOW_MEDIAN_ALL_DAYS: 'All 30 days',
  NET_FLOW_MEDIAN_POSITIVE_DAYS: 'Positive days only',
};

/** Radio-card text for the two net inflow measures (SPEC 4.3). */
export const CURE_CAP_BASIS_DESCRIPTION: Readonly<Record<LiquidityCureCapBasis, string>> = {
  NET_FLOW_MEDIAN_ALL_DAYS:
    "Median of every day's net over 30 days. A day with no posted transaction counts as $0 and a day with only debits counts below $0, so an account paid once or twice a month usually reads $0 or less, and its cure cap is $0.",
  NET_FLOW_MEDIAN_POSITIVE_DAYS:
    'Median of only the days that netted positive; for most eligible accounts these are paydays. For an account paid twice a month this reads close to one paycheck; the haircut below exists for that case.',
};

export const DATA_LANE_LABEL: Readonly<Record<LiquidityDataLaneMode, string>> = {
  CORE_ONLY: 'Core records only',
  CORE_PLUS_AGGREGATED: 'Core records plus consented aggregated accounts',
};

export const STANDBY_SEGMENT_LABEL: Readonly<Record<StandbyOfferSegment, string>> = {
  APPROACHING_BOUNDARY: 'Approaching the boundary',
  EXTERNAL_LIQUIDITY_OR_LENGTHENING_CURE: 'External liquidity or lengthening cure',
  STABLE_QUICK_CURE: 'Stable quick cure',
};

/** Tier names match tierCounts and the Account Decision page (SPEC invariant 8). */
export const TIER_LABEL: Readonly<Record<CoverageTier, string>> = {
  NONE: 'No advisory limit',
  ENTRY: 'Entry',
  STANDARD: 'Standard',
  GRADUATED_LINE_OFFER: 'Tier 2 (not produced today)',
};

export const TIER_ORDER: readonly CoverageTier[] = ['NONE', 'ENTRY', 'STANDARD', 'GRADUATED_LINE_OFFER'];

// ---------------------------------------------------------------------------
// Control copy that is not a field helper (SPEC 4.1-4.6).
// ---------------------------------------------------------------------------

export const CONTROL_COPY = {
  regENotApplied: 'Not applied: no per-item fee is charged.',
  regEOffWarningTitle: 'Opt-in check is off',
  regEOffWarningBody:
    'Under a per-item fee, accounts without a reported opt-in are no longer excluded by this check.',
  regERegulatoryLine:
    'Regulation E requires the opt-in only before a fee on an ATM or one-time debit overdraft (12 CFR 1005.17(b)(1)), and bars conditioning payment of other overdrafts on it (1005.17(b)(2)). Counsel to confirm before Production.',
  coolingOffDependency: 'No effect while charge-offs exclude with no end date.',
  standardCapNoStandardTier:
    'No account in this preview is at the standard tier, so this cap changes no advisory limit today.',
  programCeilingSwitch: 'Set a program ceiling',
  programCeilingAtOrAboveStandard: 'At or above the standard cap, this ceiling changes no advisory limit.',
  cureCapBasisUnsetTag: 'Not written in this version; the scorecard reads all 30 days',
  cureCapPreviewBoth: 'Preview both',
  haircutSwitch: 'Apply a haircut',
  multiplierSwitch: 'Record a multiplier',
  paymentCeilingSwitch: 'Record a payment ceiling',
  carriedNotRecorded: 'Not recorded on this version',
  dataLaneBadge: 'Recorded only; the scorecard does not read it',
  dataLaneUnsetTitle: 'Data source not set',
  dataLaneUnsetBody:
    'This version leaves the data source unresolved, which the policy contract treats as a configuration error. Choose one before publishing.',
  explainerTrigger: 'How this works',
  useBaselineValue: (versionNumber: number) => `Use version ${versionNumber} value`,
  baselineValue: (versionNumber: number, display: string) => `Version ${versionNumber}: ${display}`,
  haircutReadout: (keptPercent: string) => `Keeps ${keptPercent}% of the cure cap`,
  previewBothResult: (allDays: number, positiveDays: number) =>
    `All 30 days: ${allDays} ${accountsNoun(allDays)} with an advisory limit / Positive days only: ${positiveDays}`,
} as const;

/**
 * Preset captions (SPEC 4.4 with post-verify correction 2). Keyed by the stored
 * fraction, since presets are stored in contract units.
 */
export const STEP_DOWN_PRESET_CAPTION: Readonly<Record<string, string>> = {
  '0': 'Step-down allows no fall, even from data-completeness scaling.',
  '1': 'Applies the new figure at once',
};

// ---------------------------------------------------------------------------
// Advisory lines: a value outside the range the scorecard is built for. They
// never block (SPEC 2 invariant 9, 4.0 item 8). bounds.ts decides when each
// applies; the text lives here with the rest of the copy.
// ---------------------------------------------------------------------------

export type AdvisoryId = AdminPortalUiComponents['schemas']['CoveragePolicyAdvisoryId'];

export const ADVISORY_TEXT: Readonly<Record<AdvisoryId, string>> = {
  minTenureBelowZero: 'Below 0, every account passes this check.',
  regularityAboveOne: 'Above 1, every account fails this check.',
  regularityBelowZero: 'Below 0, every account with a score passes.',
  entryCapBelowZero: 'Below 0, accounts at the entry tier record $0.',
  stepDownBelowZero:
    'Below 0%, a falling figure is raised above the previous advisory limit, and above its cap when the previous figure is large enough.',
  stepDownAboveOne: 'Above 100%, this step does nothing.',
  reviewFlagAtOrBelowZero: 'At 0 or below, every account is flagged.',
  completenessAboveOne: 'Above 100%, every account is scaled and none qualifies for Tier 2 review.',
};

// ---------------------------------------------------------------------------
// Reason codes (SPEC 6.3 items 3, 5, 6).
// ---------------------------------------------------------------------------

export const REASON_LABEL: Readonly<Record<LiquidityReasonCode, string>> = {
  ELIGIBLE_BASE: 'Pass every check',
  INSUFFICIENT_TENURE: 'Observed history below the minimum',
  NO_RECURRING_INFLOW: 'Deposits not regular enough',
  PRIOR_CHARGE_OFF: 'Charge-off on record',
  REG_E_OPT_IN_MISSING: 'No Reg E opt-in on record',
  COOLING_OFF_ACTIVE: 'Waiting after a charge-off',
  LIMIT_FROM_INFLOW_ALPHA: 'Share of reliable deposits',
  LIMIT_CAPPED_BY_CURE_TIME: 'Cure cap',
  LIMIT_CAPPED_BY_TIER: 'Tier cap',
  LIMIT_CAPPED_BY_PROGRAM: 'Program ceiling',
  FEE_TO_INFLOW_BREAKER: 'Fee freeze triggered',
  STEP_DOWN_DETERIORATION: 'New figure below the previous advisory limit',
  LOW_DATA_CONFIDENCE: 'Scaled for low data',
  CHRONIC_USE_FLAG: 'Flagged for review',
  GRADUATION_QUALIFIED: 'Qualifies for Tier 2 review',
  POLICY_DISABLED: 'Delivery mode is Off',
  POLICY_NOT_EFFECTIVE: 'No version governs yet',
  DELIVERY_MODE_UNREADABLE: 'Delivery mode could not be read',
  FORECAST_UNAVAILABLE: 'Forecast unavailable',
  PRICING_EXCEEDS_REGIME_CEILING: 'Pricing above the applicable ceiling',
  MLA_MAPR_CEILING_EXCEEDED: 'Military lending APR ceiling exceeded',
  NO_LIMIT_RECOMMENDED: 'Records no advisory limit',
  ABSTAIN: 'Not scored: no balance observed',
};

/** The five checks in Section 1, in the order the engine runs them (lf:161-179). */
export const GATE_REASON_CODES = [
  'INSUFFICIENT_TENURE',
  'NO_RECURRING_INFLOW',
  'PRIOR_CHARGE_OFF',
  'REG_E_OPT_IN_MISSING',
  'COOLING_OFF_ACTIVE',
] as const satisfies readonly LiquidityReasonCode[];

/** The "Other" group of the no-advisory-limit table. */
export const OTHER_NO_LIMIT_REASON_CODES = [
  'POLICY_DISABLED',
  'POLICY_NOT_EFFECTIVE',
  'DELIVERY_MODE_UNREADABLE',
  'FORECAST_UNAVAILABLE',
  'PRICING_EXCEEDS_REGIME_CEILING',
  'MLA_MAPR_CEILING_EXCEEDED',
] as const satisfies readonly LiquidityReasonCode[];

/**
 * The four amounts, in the engine's tie order: when two amounts tie, the first
 * in this list is named (limit-function.ts:218-222).
 */
export const SMALLEST_AMOUNT_REASON_CODES = [
  'LIMIT_FROM_INFLOW_ALPHA',
  'LIMIT_CAPPED_BY_CURE_TIME',
  'LIMIT_CAPPED_BY_TIER',
  'LIMIT_CAPPED_BY_PROGRAM',
] as const satisfies readonly LiquidityReasonCode[];

export const PROTECTION_REASON_CODES = [
  'FEE_TO_INFLOW_BREAKER',
  'STEP_DOWN_DETERIORATION',
  'LOW_DATA_CONFIDENCE',
  'CHRONIC_USE_FLAG',
  'GRADUATION_QUALIFIED',
] as const satisfies readonly LiquidityReasonCode[];

// ---------------------------------------------------------------------------
// Reach badges (SPEC 6.4).
// ---------------------------------------------------------------------------

export type ReachKind = AdminPortalUiComponents['schemas']['CoveragePolicyReachKind'];

export const REACH_BY_PATH: Readonly<Partial<Record<ParameterLeafPath, { readonly code: LiquidityReasonCode; readonly kind: ReachKind }>>> = {
  alpha: { code: 'LIMIT_FROM_INFLOW_ALPHA', kind: 'smallest' },
  cureCapBasis: { code: 'LIMIT_CAPPED_BY_CURE_TIME', kind: 'smallest' },
  maxCureDays: { code: 'LIMIT_CAPPED_BY_CURE_TIME', kind: 'smallest' },
  cureCapHaircut: { code: 'LIMIT_CAPPED_BY_CURE_TIME', kind: 'smallest' },
  'tierCaps.entryCap': { code: 'LIMIT_CAPPED_BY_TIER', kind: 'smallest' },
  'tierCaps.standardCap': { code: 'LIMIT_CAPPED_BY_TIER', kind: 'smallest' },
  programCapUsd: { code: 'LIMIT_CAPPED_BY_PROGRAM', kind: 'smallest' },
  'eligibility.minTenureDays': { code: 'INSUFFICIENT_TENURE', kind: 'reason' },
  'eligibility.minRecurringInflowScore': { code: 'NO_RECURRING_INFLOW', kind: 'reason' },
  'eligibility.excludePriorChargeOff': { code: 'PRIOR_CHARGE_OFF', kind: 'reason' },
  'eligibility.requireRegEOptInForFeeCoverage': { code: 'REG_E_OPT_IN_MISSING', kind: 'reason' },
  'guardrails.coolingOffDays': { code: 'COOLING_OFF_ACTIVE', kind: 'reason' },
  'guardrails.feeToInflowBreakerRatio': { code: 'FEE_TO_INFLOW_BREAKER', kind: 'triggered' },
  'guardrails.stepDownDecrementRatio': { code: 'STEP_DOWN_DETERIORATION', kind: 'fell' },
  'guardrails.minDataCompletenessRatio': { code: 'LOW_DATA_CONFIDENCE', kind: 'scaled' },
  'guardrails.chronicUseEventThreshold12mo': { code: 'CHRONIC_USE_FLAG', kind: 'flagged' },
};

/** Paths whose badge reads "Not applied by the scorecard" (SPEC 6.4 last row). */
export const NOT_APPLIED_PATHS: readonly ParameterLeafPath[] = [
  'tierCaps.graduatedLineMultiplier',
  'eligibility.maxPaymentToInflowRatio',
  'dataLane',
];

export const NOT_APPLIED_BADGE = 'Not applied by the scorecard';
export const REACH_OUT_OF_DATE_SUFFIX = ' (out of date)';

export function accountsNoun(count: number): string {
  return count === 1 ? 'account' : 'accounts';
}

export function reachBadgeText(kind: ReachKind, count: number): string {
  const n = formatCount(count);
  switch (kind) {
    case 'smallest':
      return `Smallest amount on ${n} ${accountsNoun(count)}`;
    case 'reason':
      return `Reason on ${n} ${accountsNoun(count)}`;
    case 'triggered':
      return `Triggered on ${n}`;
    case 'fell':
      return `Figure fell on ${n}`;
    case 'scaled':
      return `Scaled ${n}`;
    case 'flagged':
      return `Flagged ${n}`;
  }
}

/**
 * Added to a reach badge when Try the range is flat across the control's stops.
 * The suffix names that control's own stop set (post-verify correction 7), so
 * it never claims more than the values actually tried.
 */
export function flatRangeSuffix(firstStop: string, lastStop: string): string {
  return `; no value from ${firstStop} to ${lastStop} changes this book`;
}

// ---------------------------------------------------------------------------
// Preview copy (SPEC 6.1-6.3).
// ---------------------------------------------------------------------------

const COVERED_LABEL = 'Accounts with an advisory limit';

/**
 * The outcome labels when a baseline run is present, so `change` is a
 * difference. Keyed by the CoveragePolicyOutcomeFigures properties, whose two
 * amounts carry tempo's LimitsDistribution names.
 */
export const OUTCOME_LABEL = {
  covered: `${COVERED_LABEL} (net change)`,
  totalRecommendedLimitUsd: 'Total advisory limits',
  medianLimitUsd: 'Median, all evaluated accounts, including $0',
  passEveryCheck: 'Pass every check',
} as const satisfies Readonly<
  Record<keyof AdminPortalUiComponents['schemas']['CoveragePolicyOutcomeFigures'], string>
>;

/** The outcome labels with no baseline run: `change` then holds the draft figure alone. */
export const OUTCOME_LABEL_NO_BASELINE: Readonly<Record<keyof typeof OUTCOME_LABEL, string>> = {
  ...OUTCOME_LABEL,
  covered: COVERED_LABEL,
};

export const PREVIEW_COPY = {
  runPreview: 'Run preview',
  notRunExplainer:
    'Evaluates the settings against the accounts scored so far, using the same scorecard that produces recommendations. Nothing is saved.',
  populationLine:
    'The scoring run covers only accounts with an active Tempo enrollment when it ran. Accounts without one are not in this preview, so these figures describe accounts in Tempo.',
  latestSnapshotLine:
    'Each account is evaluated on its latest scored snapshot with its current opt-in and wait status, so a preview can differ from the last scoring run.',
  smallestAmountHeading: 'Smallest of the four amounts',
  smallestAmountCaption:
    'For each account that passes every check, the amount that was smallest before rounding and before the protections, including accounts that record $0. The protections can change the final figure. When two amounts tie, the first in this list is named.',
  bandsHeading: 'Limits by band',
  bandsCaption: 'An advisory limit exactly at a cap falls in the band that starts at the cap.',
  noLimitHeading: 'Why accounts have no advisory limit',
  noLimitFooter:
    'An account can fail more than one check, so these rows can add to more than the accounts without an advisory limit.',
  protectionsHeading: 'Protections at work',
  tierMixHeading: 'Tier mix',
  plainLanguageHeading: 'Policy in plain language',
  savesNothing: 'The preview saves nothing.',
  pinScenario: 'Pin as scenario',
  updatePreview: 'Update preview',
  truncatedAtCap:
    'This preview covers the first 50,000 scored accounts in account order. The rest of the book is not included.',
} as const;

/** Delivery-mode note for the preview status line (SPEC 6.3 item 1). */
export const PREVIEW_DELIVERY_MODE_NOTE: Readonly<Record<TempoDeliveryMode, string>> = {
  OFF: 'Delivery mode is Off: today every account records no advisory limit. Both columns show what the settings would compute if the program were running.',
  PARALLEL_EVALUATION:
    'Parallel evaluation: recommendations are recorded, and your institution does not act on them yet.',
  PRODUCTION: 'Production: your institution acts on the recommended advisory limits.',
};

// ---------------------------------------------------------------------------
// Engine-rule messages placed by details[].path (SPEC Section 10). The two
// engine numbers (30 days, 50%) are display text only; the page never enforces
// them (SPEC 2 invariant 9). S12 moves both into the contract.
// ---------------------------------------------------------------------------

export const ENGINE_RULE_COPY = {
  tierCapsNonMonotonic: (standardCap: string) =>
    `The entry cap must not exceed the standard cap (${standardCap}).`,
  programCapBelowEntryCap: (entryCap: string) =>
    `Must be at least the entry cap (${entryCap}), or turn the program ceiling off.`,
  /** The two ends are the contract's bounds; bounds.ts reads them from the schema document. */
  graduatedMultiplierOutOfRange: (lowest: string, highest: string) =>
    `Choose a multiple from ${lowest} to ${highest}.`,
  feeBreakerOutOfRange: 'The scorecard engine accepts a share above 0% and at most 50%.',
  coolingOffBelowMinimum: 'The scorecard engine accepts at least 30 days.',
  notAccepted: 'The scorecard engine did not accept this value.',
} as const;

/**
 * Messages for a request the contract validator rejected (error.code
 * REQUEST_VALIDATION_FAILED), where the engine never saw the request. Field
 * messages for this case reuse the SPEC Section 10 client wording ("Choose
 * from ...", "Enter a value for ..."); this is the text for a path that names
 * no field on this page.
 */
export const CONTRACT_RULE_COPY = {
  notAccepted: 'The policy contract does not accept this value.',
} as const;

// ---------------------------------------------------------------------------
// SPEC Section 12: copy corrected against the engine and the register. Rows
// that are field labels or helpers live in FIELDS above; the rest are here.
// ---------------------------------------------------------------------------

export const SECTION_12_COPY = {
  noVersionNotice:
    'No Aaim Tempo coverage policy has been published for your institution yet. The settings below are starting values from the Tempo design, and none is a requirement; each shows its source. Review each one, then publish version 1.',
  multiplierNoDefault: 'There is no platform default.',
  recordedOnlyGroup: RECORDED_ONLY_GROUP_LABEL,
} as const;

/**
 * Delivery-mode descriptions for the change dialog (SPEC 8.4 and the Section 12
 * row that replaces "No member is offered a limit"). delivery-mode.ts still
 * carries the earlier wording; the dialog step adopts these.
 */
export const DELIVERY_MODE_DIALOG_DESCRIPTION: Readonly<Record<TempoDeliveryMode, string>> = {
  OFF: 'The scorecard does not compute. Each scoring run records no advisory limit for every account (reason: delivery mode is Off).',
  PARALLEL_EVALUATION:
    'The scorecard runs against the accounts in Tempo and records recommendations. Your institution does not act on them.',
  PRODUCTION: 'The scorecard runs, and your institution acts on the recommended advisory limits.',
};

// ---------------------------------------------------------------------------
// Number and value formatting (en-US).
// ---------------------------------------------------------------------------

/*
 * Every formatter below shows a stored value exactly as stored. A value is
 * first read through twelve significant digits, which removes binary noise
 * (0.07 * 100 reads 7 rather than 7.000000000000001), and is
 * then printed with as many fraction digits as that reading needs. So 0.551
 * and 0.554 read differently, and an examiner-facing threshold is the one the
 * scorecard compares against. Two fraction digits remain the minimum only
 * where the SPEC shows them: the regularity score ("0.50", SPEC 6.3 item 8
 * sentence 3), the multiplier ("1.20x", SPEC 4.5) and USD cents.
 */

/** Intl's portable ceiling on fraction digits. */
const MAX_FRACTION_DIGITS = 20;

function storedNumber(value: number): number {
  return Number.isFinite(value) ? Number(value.toPrecision(12)) : value;
}

/** Fraction digits in the shortest decimal form of a number (1.5e-7 needs 8). */
function fractionDigitsOf(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const [mantissa, exponent] = String(Math.abs(value)).split('e');
  const decimals = (mantissa.split('.')[1] ?? '').length - (exponent === undefined ? 0 : Number(exponent));
  return Math.min(MAX_FRACTION_DIGITS, Math.max(0, decimals));
}

function formatDecimal(value: number, minimumFractionDigits: number, useGrouping: boolean): string {
  const stored = storedNumber(value);
  const digits = Math.max(minimumFractionDigits, fractionDigitsOf(stored));
  return stored.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping,
  });
}

/** Whole dollars read "$500"; any other amount shows cents and every further digit it holds. */
export function formatUsd(value: number): string {
  const stored = storedNumber(value);
  const digits = Number.isInteger(stored) ? 0 : Math.max(2, fractionDigitsOf(stored));
  return stored.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatCount(value: number): string {
  return formatDecimal(value, 0, true);
}

/**
 * The percent a stored fraction displays as, without the sign: 0.07 reads 7,
 * and an off-step 0.155 reads 15.5.
 */
export function percentNumber(fraction: number): string {
  return formatDecimal(fraction * 100, 0, false);
}

export function formatPercent(fraction: number): string {
  return `${percentNumber(fraction)}%`;
}

/** "0.50"; an off-step 0.555 reads "0.555". */
export function formatScore(value: number): string {
  return formatDecimal(value, 2, false);
}

/** "1.20x"; an off-step 1.125 reads "1.125x". */
export function formatMultiple(value: number): string {
  return `${formatDecimal(value, 2, false)}x`;
}

export function daysNoun(count: number): string {
  return count === 1 ? 'day' : 'days';
}

export function formatSignedCount(change: number): string {
  if (change > 0) return `+${formatCount(change)}`;
  return formatCount(change);
}

export function formatSignedUsd(change: number): string {
  if (change > 0) return `+${formatUsd(change)}`;
  return formatUsd(change);
}

/**
 * The absence rule a contract field states, shown after "Not set" (SPEC 8.2).
 * Each is the field's own description in tempo.openapi.yaml, in page words.
 */
export const ABSENCE_RULE: Readonly<Partial<Record<ParameterLeafPath, string>>> = {
  cureCapBasis: 'reads all 30 days',
  programCapUsd: 'the standard cap is the ceiling',
  cureCapHaircut: 'removes nothing',
  'eligibility.maxPaymentToInflowRatio': 'not applied',
  dataLane: 'unresolved',
  'workoutOffer.feesStopEffectiveAt': "takes effect with the version's effective date",
};

export const NOT_SET = 'Not set';

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

function enumLabel(record: Readonly<Record<string, string>>, value: unknown): string {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(record, value)
    ? record[value]
    : String(value);
}

/**
 * Display text for one stored value. An absent value reads "Not set", followed
 * by the contract's absence rule where the field states one. Values are shown
 * as stored: an off-step 0.155 reads 15.5%, and 0.551 and 0.554 read apart.
 */
export function formatFieldValue(path: ParameterLeafPath, value: unknown): string {
  if (value === undefined || value === null) {
    const rule = ABSENCE_RULE[path];
    return rule ? `${NOT_SET} (${rule})` : NOT_SET;
  }
  const { unit } = FIELDS[path];
  if (typeof value === 'number') {
    switch (unit) {
      case 'fraction-percent':
        return formatPercent(value);
      case 'percent':
        return `${formatCount(value)}%`;
      case 'usd':
        return formatUsd(value);
      case 'days':
        return `${formatCount(value)} ${daysNoun(value)}`;
      case 'day-number':
        return `Day ${formatCount(value)}`;
      case 'months':
        return `${formatCount(value)} ${value === 1 ? 'month' : 'months'}`;
      case 'score':
        return formatScore(value);
      case 'multiple':
        return formatMultiple(value);
      default:
        return formatCount(value);
    }
  }
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (path === 'pricingMode') return enumLabel(PRICING_MODE_LABEL, value);
  if (path === 'lineTerms.pricingStructure') return enumLabel(LINE_PRICING_STRUCTURE_LABEL, value);
  if (path === 'cureCapBasis') return enumLabel(CURE_CAP_BASIS_LABEL, value);
  if (path === 'dataLane') return enumLabel(DATA_LANE_LABEL, value);
  if (path === 'standbyTargeting.offerSegments' && Array.isArray(value)) {
    return value.length === 0 ? 'None' : value.map((segment) => enumLabel(STANDBY_SEGMENT_LABEL, segment)).join(', ');
  }
  if (Array.isArray(value)) return `${formatCount(value.length)} ${value.length === 1 ? 'rule' : 'rules'}`;
  if (unit === 'date' && typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : DATE_FORMAT.format(parsed);
  }
  if (typeof value === 'string') return value;
  // A leaf path holding an object means the stored document does not match the
  // contract. Show it as stored (its JSON), never as "[object Object]".
  return JSON.stringify(value);
}
